import React, { useState, useCallback, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import SiteNav from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';
import ScrollToTop from '../components/ScrollToTop';
import { useSEO } from '../hooks/useSEO';
import { db, auth } from '../services/firebase';
import { trackDayPlanGenerated, trackDayPlanSaved, trackGenerateFail } from '../services/trackingService';
import { collection, addDoc, doc, getDoc, serverTimestamp, increment, updateDoc } from 'firebase/firestore';
import { MapPin, Users, Sparkles, DollarSign, Clock, ChevronDown, ChevronUp, ArrowRight, Navigation, Printer, RotateCcw, ChevronLeft, ChevronRight } from 'lucide-react';
import { captureLead, trackFunnel } from '../utils/leadClient';

// ── Types ───────────────────────────────────────────────────────────────────
interface DayPlanFormData {
  destination: string;
  crew: string;
  vibe: string[];
  budget: string;
  notes: string;
}

interface DayStop {
  time: string;
  name: string;
  category: string;
  description: string;
  tip: string;
  estimatedCost: string;
  address: string;
}

interface DayPlan {
  title: string;
  tagline: string;
  totalBudget: string;
  stops: DayStop[];
}

// ── Destination Images ──────────────────────────────────────────────────────
const DESTINATION_IMAGES: Record<string, string> = {
  paris: 'photo-1502602898657-3e91760cbb34', london: 'photo-1513635269975-59663e0ac1ad',
  tokyo: 'photo-1540959733332-eab4deabeeaf', rome: 'photo-1552832230-c0197dd311b5',
  'new york': 'photo-1496442226666-8d4d0e62e6e9', barcelona: 'photo-1583422409516-2895a77efded',
  dubai: 'photo-1512453979798-5ea266f8880c', bali: 'photo-1537996194471-e657df975ab4',
  sydney: 'photo-1506973035872-a4ec16b8e8d9', amsterdam: 'photo-1534351590666-13e3e96b5017',
  istanbul: 'photo-1524231757912-21f4fe3a7200', bangkok: 'photo-1508009603885-50cf7c579365',
  lisbon: 'photo-1558369981-f9ca78462e61', singapore: 'photo-1525625293386-3f8f99389edd',
  marrakech: 'photo-1597212618440-806262de4f6b', santorini: 'photo-1570077188670-e3a8d69ac5ff',
  kyoto: 'photo-1493976040374-85c8e12f0c0e', maldives: 'photo-1514282401047-d79a71a590e8',
};
const TRAVEL_FALLBACKS = ['photo-1488646953014-85cb44e25828','photo-1507525428034-b723cf961d3e','photo-1476514525535-07fb3b4ae5f1','photo-1469854523086-cc02fe5d8800'];
const getDestinationImage = (dest: string, width = 1200): string => {
  const key = dest.toLowerCase().trim();
  const exact = DESTINATION_IMAGES[key];
  if (exact) return `https://images.unsplash.com/${exact}?q=80&w=${width}&auto=format&fit=crop`;
  const partial = Object.entries(DESTINATION_IMAGES).find(([k]) => key.includes(k) || k.includes(key));
  if (partial) return `https://images.unsplash.com/${partial[1]}?q=80&w=${width}&auto=format&fit=crop`;
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = ((hash << 5) - hash + key.charCodeAt(i)) | 0;
  return `https://images.unsplash.com/${TRAVEL_FALLBACKS[Math.abs(hash) % TRAVEL_FALLBACKS.length]}?q=80&w=${width}&auto=format&fit=crop`;
};

// ── Constants ───────────────────────────────────────────────────────────────
const AURA_VIBES = [
  { id: 'foodie', label: 'Food & Drinks', emoji: '🍷', desc: 'Restaurants, cafés & local flavors' },
  { id: 'outdoors', label: 'Outdoors & Nature', emoji: '🌿', desc: 'Parks, trails & fresh air' },
  { id: 'culture', label: 'Arts & Culture', emoji: '🎨', desc: 'Museums, galleries & history' },
  { id: 'relaxed', label: 'Relaxed & Easy', emoji: '☕', desc: 'Slow pace, no rush' },
  { id: 'adventure', label: 'Adventure & Active', emoji: '⚡', desc: 'Get moving & try something new' },
  { id: 'nightlife', label: 'Nightlife', emoji: '🌙', desc: 'Bars, rooftops & late nights' },
  { id: 'shopping', label: 'Shopping', emoji: '🛍️', desc: 'Boutiques, markets & finds' },
];
const BUDGETS = ['Keep it lean', 'Moderate ($50–150)', 'Treat yourself ($150–300)', 'Money is no object'];
const CREWS = ['Solo', 'Partner / date', 'Friends', 'Family with kids', 'Work crew'];

const LOADING_MESSAGES = [
  'Curating your perfect day...',
  'Scouting local gems...',
  'Mapping your itinerary...',
  'Polishing the details...',
  'Almost ready...',
];

// ── Email Capture Component ─────────────────────────────────────────────────
const EmailCapture: React.FC<{ destination: string; planTitle: string; formData?: DayPlanFormData }> = ({ destination, planTitle, formData }) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes('@') || status === 'sending') return;
    setStatus('sending');
    const cleanEmail = email.toLowerCase().trim();
    try {
      // 1. Capture the lead (system of record + ESP sync, server-side)
      await captureLead({
        email: cleanEmail,
        destination,
        partyType: formData?.crew,
        vibes: formData?.vibe,
        budget: formData?.budget,
        source: 'day-planner',
        tierIntent: 'free',
      });

      // 2. Actually generate + email the plan (server-side, async)
      if (formData) {
        await fetch('/api/day-planner/email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: cleanEmail, formData }),
        });
      }
      trackFunnel('email_capture', { source: 'day-planner', destination });
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="max-w-xl mx-auto px-6 sm:px-8 mb-12 text-center">
        <div className="liquid-glass rounded-2xl p-6 sm:p-8">
          <svg className="w-8 h-8 text-emerald-400 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/></svg>
          <p className="font-display text-xl text-ivory mb-1" style={{ fontWeight: 300 }}>Check your inbox</p>
          <p className="font-sans text-xs text-ivory/40">We'll send your {destination} day plan shortly.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto px-6 sm:px-8 mb-12">
      <div className="liquid-glass rounded-2xl p-6 sm:p-8">
        <div className="text-center mb-5">
          <p className="font-display text-xl text-ivory mb-1" style={{ fontWeight: 300 }}>Get this plan in your inbox</p>
          <p className="font-sans text-xs text-ivory/40">We'll email you this day plan so you have it when you need it.</p>
        </div>
        <form onSubmit={handleSubmit} className="flex gap-3 max-w-sm mx-auto">
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="your@email.com"
            aria-label="Email address"
            className="flex-1 bg-white/5 border border-ivory/10 rounded-xl px-4 py-3 text-ivory text-sm font-sans placeholder:text-ivory/25 focus:border-gold/40 focus:outline-none"
          />
          <button
            type="submit"
            disabled={status === 'sending' || !email.includes('@')}
            className="px-5 py-3 bg-gold text-charcoal font-sans text-[10px] uppercase tracking-[0.12em] font-bold rounded-xl hover:bg-gold/90 disabled:opacity-40 shrink-0"
          >
            {status === 'sending' ? '...' : 'Send'}
          </button>
        </form>
        {status === 'error' && <p className="text-center text-xs text-red-400 mt-2">Something went wrong. Try again.</p>}
      </div>
    </div>
  );
};

// ── Failure Recovery Capture ────────────────────────────────────────────────
// A failed generation must never be a silent dead end. If the browser request
// fails, offer to email the plan — the server retries generation and delivers.
const RecoveryCapture: React.FC<{ formData: DayPlanFormData | null; onReset: () => void }> = ({ formData, onReset }) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes('@') || status === 'sending' || !formData) return;
    setStatus('sending');
    const cleanEmail = email.toLowerCase().trim();
    try {
      await captureLead({
        email: cleanEmail,
        destination: formData.destination,
        partyType: formData.crew,
        vibes: formData.vibe,
        budget: formData.budget,
        source: 'day-planner',
        tierIntent: 'free',
        status: 'failed_recovery',
        tags: ['generation_failed'],
      });
      await fetch('/api/day-planner/email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: cleanEmail, formData, recovery: true }),
      });
      trackFunnel('email_capture', { source: 'day-planner', recovery: true, destination: formData.destination });
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="liquid-glass rounded-2xl p-6 sm:p-8 max-w-md mx-auto text-center">
        <svg className="w-8 h-8 text-emerald-400 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/></svg>
        <p className="font-display text-xl text-ivory mb-1" style={{ fontWeight: 300 }}>On its way</p>
        <p className="font-sans text-xs text-ivory/40">We're finishing your {formData?.destination} day plan and will email it to you shortly.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="liquid-glass rounded-2xl p-6 sm:p-8 max-w-md mx-auto">
      <p className="font-display text-lg text-ivory mb-1 text-center" style={{ fontWeight: 300 }}>Get it emailed the moment it's ready</p>
      <p className="font-sans text-xs text-ivory/40 text-center mb-5">Our servers will finish your {formData?.destination} plan and send it over — no need to wait here.</p>
      <div className="flex gap-3 max-w-sm mx-auto">
        <input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="your@email.com"
          aria-label="Email address"
          className="flex-1 bg-white/5 border border-ivory/10 rounded-xl px-4 py-3 text-ivory text-sm font-sans placeholder:text-ivory/25 focus:border-gold/40 focus:outline-none"
        />
        <button type="submit" disabled={status === 'sending' || !email.includes('@')} className="px-5 py-3 bg-gold text-charcoal font-sans text-[10px] uppercase tracking-[0.12em] font-bold rounded-xl hover:bg-gold/90 disabled:opacity-40 shrink-0">
          {status === 'sending' ? '...' : 'Email it'}
        </button>
      </div>
      {status === 'error' && <p className="text-center text-xs text-red-400 mt-2">Something went wrong. Try again.</p>}
      <button onClick={onReset} type="button" className="block mx-auto mt-4 font-sans text-[10px] uppercase tracking-[0.15em] text-ivory/40 hover:text-gold transition-colors">Or try again now</button>
    </form>
  );
};

// ── Form Component ──────────────────────────────────────────────────────────
const DayPlanForm: React.FC<{ onSubmit: (data: DayPlanFormData) => void }> = ({ onSubmit }) => {
  const [destination, setDestination] = useState('');
  const [crew, setCrew] = useState('');
  const [vibe, setVibe] = useState<string[]>([]);
  const [budget, setBudget] = useState('');
  const [notes, setNotes] = useState('');

  const toggleVibe = (id: string) => setVibe(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const isValid = destination.trim() && crew && vibe.length > 0 && budget;
  const heroImg = destination.trim().length > 2 ? getDestinationImage(destination, 1600) : null;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
      {/* Hero — destination image when typed, gradient fallback */}
      <div className="relative w-full min-h-[380px] overflow-hidden" style={heroImg ? undefined : { background: 'linear-gradient(135deg, #0A0A0A 0%, #1a1510 40%, #0f1a1a 70%, #0A0A0A 100%)' }}>
        {heroImg && (
          <>
            <img src={heroImg} alt="" className="absolute inset-0 w-full h-full object-cover transition-all duration-1000" />
            <div className="absolute inset-0 bg-gradient-to-b from-charcoal/50 via-charcoal/30 to-charcoal" />
          </>
        )}
        {!heroImg && <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, rgba(201,169,110,0.4) 0%, transparent 50%), radial-gradient(circle at 70% 30%, rgba(100,140,180,0.3) 0%, transparent 50%)' }} />}
        <div className="relative pt-28 pb-14 px-6 sm:px-8">
          <div className="max-w-2xl mx-auto w-full">
            <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-gold mb-5">✦ Free Day Planner</p>
            {destination.trim() ? (
              <>
                <p className="font-sans text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Your day in</p>
                <h1 className="font-display text-5xl sm:text-6xl md:text-7xl text-white leading-[0.95] mb-4" style={{ fontWeight: 300, textShadow: heroImg ? '0 2px 30px rgba(0,0,0,0.6)' : 'none' }}>
                  {destination.trim()}
                </h1>
              </>
            ) : (
              <h1 className="font-display text-4xl sm:text-5xl md:text-6xl text-ivory leading-[1.05] mb-4" style={{ fontWeight: 300 }}>
                Design your<br /><em className="font-serif text-gold">perfect day.</em>
              </h1>
            )}
            <p className="font-sans text-sm text-white/60 max-w-lg" style={{ lineHeight: 1.8 }}>
              Tell us your destination and vibe — we'll craft a single-day itinerary with real places, insider tips, and cost estimates.
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-2xl mx-auto px-6 sm:px-8">
        <div className="flex flex-col gap-10 py-12">
          {/* Destination */}
          <div>
            <label className="block font-sans text-[10px] uppercase tracking-[0.15em] text-gold/60 mb-3">
              <MapPin className="w-3.5 h-3.5 inline mr-2" />Where to?
            </label>
            <input
              className="w-full bg-white/5 border border-ivory/10 rounded-xl px-5 py-4 text-ivory text-base font-sans placeholder:text-ivory/25 focus:border-gold/40 focus:outline-none transition-colors"
              placeholder="Paris, Tokyo, Marrakech..."
              value={destination}
              onChange={e => setDestination(e.target.value)}
            />
          </div>

          {/* Crew */}
          <div>
            <label className="block font-sans text-[10px] uppercase tracking-[0.15em] text-gold/60 mb-3">
              <Users className="w-3.5 h-3.5 inline mr-2" />Who's joining you?
            </label>
            <div className="flex flex-wrap gap-2">
              {CREWS.map(c => (
                <button key={c} onClick={() => setCrew(c)}
                  className={`px-5 py-2.5 rounded-full text-xs font-sans border transition-all ${
                    crew === c ? 'bg-gold text-charcoal border-gold font-medium' : 'bg-transparent text-ivory/50 border-ivory/10 hover:border-gold/30 hover:text-ivory'
                  }`}
                >{c}</button>
              ))}
            </div>
          </div>

          {/* Aura / Vibe */}
          <div>
            <label className="block font-display text-lg text-ivory mb-1" style={{ fontWeight: 300 }}>
              What's your <em className="font-serif text-gold">aura</em> today?
            </label>
            <p className="font-sans text-[10px] uppercase tracking-[0.15em] text-ivory/30 mb-4">Select all that resonate</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {AURA_VIBES.map(v => (
                <button key={v.id} onClick={() => toggleVibe(v.label)}
                  className={`liquid-glass p-4 rounded-xl border text-left transition-all group ${
                    vibe.includes(v.label) ? 'border-gold/40 bg-gold/10' : 'border-ivory/5 hover:border-ivory/15'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{v.emoji}</span>
                    <div>
                      <p className={`font-sans text-sm font-medium ${vibe.includes(v.label) ? 'text-gold' : 'text-ivory/80 group-hover:text-ivory'}`}>{v.label}</p>
                      <p className="font-serif text-[11px] text-ivory/40 italic">{v.desc}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Budget */}
          <div>
            <label className="block font-sans text-[10px] uppercase tracking-[0.15em] text-gold/60 mb-3">
              <DollarSign className="w-3.5 h-3.5 inline mr-2" />Budget range
            </label>
            <div className="flex flex-wrap gap-2">
              {BUDGETS.map(b => (
                <button key={b} onClick={() => setBudget(b)}
                  className={`px-5 py-2.5 rounded-full text-xs font-sans border transition-all ${
                    budget === b ? 'bg-gold text-charcoal border-gold font-medium' : 'bg-transparent text-ivory/50 border-ivory/10 hover:border-gold/30 hover:text-ivory'
                  }`}
                >{b}</button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block font-sans text-[10px] uppercase tracking-[0.15em] text-gold/60 mb-3">
              Special requests <span className="text-ivory/30 normal-case">(optional)</span>
            </label>
            <textarea
              className="w-full bg-white/5 border border-ivory/10 rounded-xl px-5 py-4 text-ivory text-sm font-sans placeholder:text-ivory/25 focus:border-gold/40 focus:outline-none transition-colors resize-none"
              style={{ height: 90 }}
              placeholder="Dietary needs, must-see spots, occasions to celebrate..."
              value={notes}
              onChange={e => setNotes(e.target.value)}
            />
          </div>

          {/* Submit */}
          <div className="flex flex-col gap-3 pt-2">
            <button
              disabled={!isValid}
              onClick={() => isValid && onSubmit({ destination, crew, vibe, budget, notes })}
              className="w-full bg-gold text-charcoal py-5 font-sans text-xs uppercase tracking-[0.15em] font-bold rounded-xl transition-all hover:bg-gold/90 disabled:opacity-30 disabled:cursor-not-allowed shadow-lg shadow-gold/10"
            >
              ✦ Craft My Day
            </button>
            {!isValid && (
              <p className="text-center text-xs text-ivory/30">Fill in destination, aura, and budget to continue</p>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ── Loading Component ───────────────────────────────────────────────────────
const DayPlanLoading: React.FC<{ formData: DayPlanFormData }> = ({ formData }) => {
  const [msgIndex, setMsgIndex] = useState(0);

  React.useEffect(() => {
    const interval = setInterval(() => setMsgIndex(i => Math.min(i + 1, LOADING_MESSAGES.length - 1)), 1400);
    return () => clearInterval(interval);
  }, []);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-[70vh] flex flex-col items-center justify-center gap-12 px-8">
      <div className="relative w-28 h-28">
        <div className="absolute inset-0 rounded-full border border-gold/15 animate-spin" style={{ animationDuration: '8s' }} />
        <div className="absolute inset-3 rounded-full border border-gold/20 animate-spin" style={{ animationDuration: '5s', animationDirection: 'reverse' }} />
        <div className="absolute inset-7 rounded-full bg-gradient-radial from-gold/20 to-transparent" />
        <span className="absolute inset-0 flex items-center justify-center text-gold text-xl">✦</span>
      </div>
      <div className="text-center">
        <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-gold mb-5">Aura Traveler</p>
        <h2 className="font-display text-3xl text-ivory mb-3" style={{ fontWeight: 300 }}>{LOADING_MESSAGES[msgIndex]}</h2>
        <p className="font-sans text-sm text-ivory/40">Building a day around <em className="text-ivory/60">{formData.destination}</em></p>
      </div>
      <div className="flex flex-wrap gap-2 justify-center max-w-sm">
        {formData.vibe.map(v => <span key={v} className="px-3 py-1.5 rounded-full text-[11px] font-sans border border-gold/15 text-ivory/40">{v}</span>)}
        <span className="px-3 py-1.5 rounded-full text-[11px] font-sans border border-gold/15 text-ivory/40">{formData.crew}</span>
      </div>
    </motion.div>
  );
};

// ── Time-based gradient for stop cards ──────────────────────────────────────
const getTimeGradient = (time: string): string => {
  const hour = parseInt(time);
  if (hour < 10) return 'from-amber-900/30 via-orange-900/10 to-transparent'; // morning warmth
  if (hour < 14) return 'from-gold/20 via-amber-900/10 to-transparent'; // midday gold
  if (hour < 18) return 'from-blue-900/20 via-indigo-900/10 to-transparent'; // afternoon cool
  return 'from-purple-900/25 via-indigo-900/15 to-transparent'; // evening deep
};

const CATEGORY_EMOJI: Record<string, string> = {
  breakfast: '🥐', brunch: '🥂', lunch: '🍽️', dinner: '🕯️', bar: '🍸', cafe: '☕', coffee: '☕',
  restaurant: '🍽️', bakery: '🥐', museum: '🏛️', gallery: '🎨', park: '🌿', garden: '🌺',
  market: '🛍️', shopping: '🛍️', activity: '⚡', tour: '🚶', temple: '⛩️', church: '⛪',
  beach: '🏖️', sunset: '🌅', rooftop: '🌃', spa: '💆', default: '📍',
};
const getCategoryEmoji = (cat: string): string => {
  const key = cat.toLowerCase();
  return Object.entries(CATEGORY_EMOJI).find(([k]) => key.includes(k))?.[1] || CATEGORY_EMOJI.default;
};

// ── Stop Card ───────────────────────────────────────────────────────────────
const StopPanel: React.FC<{ stop: DayStop; index: number; total: number }> = ({ stop, index, total }) => {
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(stop.address || stop.name)}`;
  const gradient = getTimeGradient(stop.time);
  const emoji = getCategoryEmoji(stop.category);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.08, duration: 0.4 }}
      className="snap-center shrink-0 w-[85vw] max-w-[520px]"
    >
      <div className={`liquid-glass rounded-2xl border border-ivory/[0.06] overflow-hidden h-full bg-gradient-to-br ${gradient}`}>
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-ivory/[0.04]">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <span className="text-2xl">{emoji}</span>
              <div>
                <p className="font-sans text-[10px] uppercase tracking-[0.15em] text-gold font-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>{stop.time}</p>
                <p className="font-sans text-[10px] uppercase tracking-widest text-ivory/30">{stop.category}</p>
              </div>
            </div>
            <span className="font-sans text-[10px] uppercase tracking-widest text-ivory/20">{index + 1} / {total}</span>
          </div>
          <h3 className="font-display text-2xl text-ivory" style={{ fontWeight: 400, lineHeight: 1.25 }}>{stop.name}</h3>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          <p className="font-sans text-sm text-ivory/60 mb-5" style={{ lineHeight: 1.85 }}>{stop.description}</p>

          {/* Insider tip */}
          <div className="rounded-xl bg-gold/[0.06] border border-gold/10 px-4 py-3.5 mb-5">
            <div className="flex items-start gap-2.5">
              <span className="text-gold text-xs mt-0.5 shrink-0">✦</span>
              <div>
                <p className="font-sans text-[10px] uppercase tracking-widest text-gold/60 mb-1">Insider tip</p>
                <p className="text-xs font-serif text-ivory/50 italic" style={{ lineHeight: 1.7 }}>{stop.tip}</p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-2">
            <div>
              <p className="font-sans text-[10px] uppercase tracking-widest text-ivory/25 mb-0.5">Est. cost</p>
              <p className="font-sans text-sm text-gold font-medium">{stop.estimatedCost}</p>
            </div>
            <a href={mapsUrl} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-ivory/10 text-[10px] font-sans uppercase tracking-[0.1em] text-ivory/40 hover:text-gold hover:border-gold/30 transition-colors"
            >
              <Navigation className="w-3 h-3" />Directions
            </a>
          </div>

          {/* Address */}
          <p className="font-sans text-[10px] text-ivory/20 mt-4 truncate">{stop.address}</p>
        </div>
      </div>
    </motion.div>
  );
};

// ── Result Component ────────────────────────────────────────────────────────
const DayPlanResult: React.FC<{ plan: DayPlan; formData: DayPlanFormData; onReset: () => void; savedPlanId?: string | null; onSave?: () => void; isSaving?: boolean }> = ({ plan, formData, onReset, savedPlanId, onSave, isSaving }) => {
  const trackRef = React.useRef<HTMLDivElement>(null);
  const scrollTo = (dir: 'left' | 'right') => {
    if (!trackRef.current) return;
    const scrollAmount = trackRef.current.offsetWidth * 0.85;
    trackRef.current.scrollBy({ left: dir === 'right' ? scrollAmount : -scrollAmount, behavior: 'smooth' });
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
      {/* Hero — destination image with overlay */}
      <div className="relative w-full min-h-[400px] overflow-hidden">
        <img
          src={getDestinationImage(formData.destination, 1600)}
          alt={formData.destination}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/60 via-charcoal/40 to-charcoal" />
        <div className="relative pt-28 pb-16 px-6 sm:px-8">
          <div className="max-w-4xl mx-auto w-full">
            <div className="flex items-center gap-3 mb-5 flex-wrap">
              <span className="font-sans text-[10px] uppercase tracking-[0.2em] text-gold">{formData.destination}</span>
              <span className="w-1 h-1 rounded-full bg-gold/30" />
              <span className="font-sans text-[10px] uppercase tracking-[0.15em] text-white/50">{formData.crew}</span>
              <span className="w-1 h-1 rounded-full bg-gold/30" />
              <span className="font-sans text-[10px] uppercase tracking-[0.15em] text-white/50">{formData.vibe.slice(0, 2).join(' · ')}</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl text-white mb-3" style={{ fontWeight: 300, textShadow: '0 2px 30px rgba(0,0,0,0.5)' }}>{plan.title}</h1>
            <p className="font-serif text-lg text-white/60 italic max-w-xl" style={{ textShadow: '0 1px 10px rgba(0,0,0,0.4)' }}>{plan.tagline}</p>
          </div>
        </div>
      </div>

      {/* Stats bar */}
      <div className="max-w-4xl mx-auto px-6 sm:px-8 -mt-6 relative z-10">
        <div className="liquid-glass-dark rounded-2xl p-5 flex items-center justify-around">
          <div className="text-center">
            <p className="font-sans text-[10px] uppercase tracking-[0.15em] text-ivory/40 mb-1">Stops</p>
            <p className="font-display text-2xl text-gold">{plan.stops.length}</p>
          </div>
          <div className="w-px h-10 bg-ivory/10" />
          <div className="text-center">
            <p className="font-sans text-[10px] uppercase tracking-[0.15em] text-ivory/40 mb-1">Est. spend</p>
            <p className="font-display text-2xl text-gold">{plan.totalBudget}</p>
          </div>
          <div className="w-px h-10 bg-ivory/10" />
          <div className="text-center">
            <p className="font-sans text-[10px] uppercase tracking-[0.15em] text-ivory/40 mb-1">Duration</p>
            <p className="font-display text-2xl text-gold">Full Day</p>
          </div>
        </div>
      </div>

      {/* Save & Share bar */}
      <div className="max-w-4xl mx-auto px-6 sm:px-8 mt-8 mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          {savedPlanId ? (
            <button
              onClick={() => {
                const url = `${window.location.origin}/day-plan/${savedPlanId}`;
                if (navigator.share) { navigator.share({ title: plan.title, url }).catch(() => {}); }
                else { navigator.clipboard.writeText(url); }
              }}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gold text-charcoal font-sans text-[10px] uppercase tracking-[0.12em] font-bold rounded-xl hover:bg-gold/90 transition-all"
            >
              <Navigation className="w-3.5 h-3.5" /> Share Day Plan
            </button>
          ) : auth?.currentUser ? (
            <button
              onClick={onSave}
              disabled={isSaving}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gold text-charcoal font-sans text-[10px] uppercase tracking-[0.12em] font-bold rounded-xl hover:bg-gold/90 transition-all disabled:opacity-40"
            >
              {isSaving ? <><div className="w-3 h-3 border-2 border-charcoal border-t-transparent rounded-full animate-spin" /> Saving...</> : '✦ Save to Vault (0.2 tickets)'}
            </button>
          ) : (
            <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 border border-ivory/15 text-ivory/40 font-sans text-[10px] uppercase tracking-[0.12em] rounded-xl hover:border-gold/30 hover:text-ivory transition-all">
              Log in to save this plan
            </Link>
          )}
        </div>
      </div>

      {/* Section label */}
      <div className="max-w-4xl mx-auto px-6 sm:px-8 mt-4 mb-6 flex items-center justify-between">
        <div>
          <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-gold/50 mb-1">Your Itinerary</p>
          <p className="font-display text-2xl text-ivory" style={{ fontWeight: 300 }}>Swipe to explore</p>
        </div>
        <div className="hidden sm:flex gap-2">
          <button onClick={() => scrollTo('left')} className="w-10 h-10 rounded-full border border-ivory/10 flex items-center justify-center text-ivory/40 hover:text-gold hover:border-gold/30 transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button onClick={() => scrollTo('right')} className="w-10 h-10 rounded-full border border-ivory/10 flex items-center justify-center text-ivory/40 hover:text-gold hover:border-gold/30 transition-colors">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Swipeable Stop Panels */}
      <div className="relative mb-16">
        <div ref={trackRef} className="flex gap-5 overflow-x-auto scroll-snap-x snap-mandatory pb-4 px-[7.5vw] scrollbar-hide" style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}>
          {plan.stops.map((stop, i) => (
            <StopPanel key={i} stop={stop} index={i} total={plan.stops.length} />
          ))}
        </div>
      </div>

      {/* Email Capture */}
      <EmailCapture destination={formData.destination} planTitle={plan.title} formData={formData} />

      {/* CTA Footer */}
      <div className="max-w-2xl mx-auto px-6 sm:px-8 pb-20 text-center">
        <div className="liquid-glass rounded-2xl p-8 sm:p-12">
          <p className="font-display text-3xl text-ivory mb-3" style={{ fontWeight: 300, fontStyle: 'italic' }}>Love this day?</p>
          <p className="font-sans text-sm text-ivory/40 mb-8 max-w-md mx-auto">Build a full multi-day luxury itinerary with AI-generated images, flights, hotels, and a shareable dossier.</p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/" className="inline-flex items-center gap-2 bg-gold text-charcoal px-8 py-4 font-sans text-[11px] uppercase tracking-[0.12em] font-bold rounded-xl transition-all hover:bg-gold/90 shadow-lg shadow-gold/10">
              Build Full Itinerary <ArrowRight className="w-4 h-4" />
            </Link>
            <button onClick={onReset} className="inline-flex items-center gap-2 px-6 py-4 border border-ivory/15 text-ivory/50 rounded-xl font-sans text-[11px] uppercase tracking-[0.1em] hover:border-gold/30 hover:text-ivory transition-all">
              <RotateCcw className="w-3.5 h-3.5" /> Plan Another Day
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ── Main Page ───────────────────────────────────────────────────────────────
const DayPlannerPage: React.FC = () => {
  const [step, setStep] = useState<'form' | 'loading' | 'result' | 'error'>('form');
  const [formData, setFormData] = useState<DayPlanFormData | null>(null);
  const [plan, setPlan] = useState<DayPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savedPlanId, setSavedPlanId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const { planId: urlPlanId } = useParams();
  const navigate = useNavigate();

  // Load saved plan from URL if present (/day-plan/:planId)
  useEffect(() => {
    if (!urlPlanId || !db) return;
    const loadPlan = async () => {
      try {
        setStep('loading');
        const docSnap = await getDoc(doc(db, 'day_plans', urlPlanId));
        if (docSnap.exists()) {
          const data = docSnap.data();
          setPlan(data.plan as DayPlan);
          setFormData(data.formData as DayPlanFormData);
          setSavedPlanId(urlPlanId);
          setStep('result');
        } else {
          setError('This day plan has expired or does not exist.');
          setStep('error');
        }
      } catch (err) {
        console.error('Failed to load saved day plan:', err);
        setError('Could not load this day plan.');
        setStep('error');
      }
    };
    loadPlan();
  }, [urlPlanId]);

  // Save to vault (0.2 tickets)
  const handleSave = useCallback(async () => {
    if (!db || !auth?.currentUser || !plan || !formData || isSaving) return;
    setIsSaving(true);
    try {
      // Deduct 0.2 credits
      const userDocRef = doc(db, 'users', auth.currentUser.uid);
      await updateDoc(userDocRef, { credits: increment(-0.2) });

      // Save the day plan
      const docRef = await addDoc(collection(db, 'day_plans'), {
        plan,
        formData,
        userId: auth.currentUser.uid,
        ownerEmail: auth.currentUser.email || '',
        isPublic: true,
        createdAt: serverTimestamp(),
      });

      setSavedPlanId(docRef.id);
      trackDayPlanSaved();
      navigate(`/day-plan/${docRef.id}`, { replace: true });
    } catch (err) {
      console.error('Failed to save day plan:', err);
    } finally {
      setIsSaving(false);
    }
  }, [db, plan, formData, isSaving, navigate]);

  useSEO({
    title: 'Day Planner — AI-Powered Daily Itinerary | Aura Traveler',
    description: 'Plan your perfect day in any city. AI-powered day planner that creates personalized itineraries with local restaurants, activities, and insider tips — free, no sign-up required.',
    canonical: 'https://www.auratraveler.com/day-planner',
    breadcrumbs: [
      { name: 'Home', url: 'https://www.auratraveler.com/' },
      { name: 'Day Planner', url: 'https://www.auratraveler.com/day-planner' },
    ],
  });

  const buildPrompt = (data: DayPlanFormData): string => {
    const vibeStr = data.vibe.join(', ');
    return `You are Aura Traveler, a luxury AI travel planner. Generate a full-day itinerary based on these preferences:

Destination: ${data.destination}
Traveling with: ${data.crew}
Vibe: ${vibeStr}
Budget: ${data.budget}
Notes: ${data.notes || 'None'}

Return ONLY a valid JSON object (no markdown, no backticks, no explanation) with this exact structure:
{
  "title": "Short catchy trip title (3-6 words)",
  "tagline": "One evocative sentence describing the day ahead",
  "totalBudget": "Estimated total spend range e.g. $80-120",
  "stops": [
    {
      "time": "9:00 AM",
      "name": "Place Name",
      "category": "Brunch / Bar / Activity / Museum / etc",
      "description": "2 sentence description of what to do and expect here. Be specific and vivid.",
      "tip": "One insider tip that only a local would know",
      "estimatedCost": "$20-30 per person",
      "address": "Full street address"
    }
  ]
}

Include 5-6 stops that flow naturally through the day (morning to evening). Use REAL places that actually exist in ${data.destination}. Make it feel curated and personal, not generic. Addresses must be real.

CRITICAL VENUE RULES:
1. Each stop MUST be exactly ONE real venue or place. NEVER combine two venues with "&", "and", "in", or "/".
   WRONG: "Three Points Bowling & Nona Social" or "Lake Nona Town Center & Foxtail Coffee"
   CORRECT: "Foxtail Coffee Co." as one stop, "Lake Nona Town Center" as a separate stop
2. The "name" field must be the venue's ACTUAL business name as it appears on Google Maps.
3. The "address" field must be the venue's real street address — do not guess or fabricate.
4. Do NOT invent venue names. If unsure a venue exists, use a well-known establishment instead.
5. If two interesting places are near each other, make them SEPARATE stops with a note about proximity in the tip field.`;
  };

  const handleSubmit = useCallback(async (data: DayPlanFormData) => {
    if (!navigator.onLine) {
      setError('You appear to be offline. Please check your connection and try again.');
      return;
    }
    setFormData(data);
    setStep('loading');
    setError(null);
    trackFunnel('generate_start', { source: 'day-planner', destination: data.destination });

    try {
      // RACE both strategies simultaneously — use whichever responds first
      // This eliminates the 30s wait if proxy is slow/down
      const proxyPromise = (async (): Promise<DayPlan | null> => {
        try {
          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), 25000);
          const res = await fetch('/api/day-planner', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
            signal: controller.signal,
          });
          clearTimeout(timeout);
          if (!res.ok) {
            const body = await res.json().catch(() => null);
            throw new Error(body?.error || `Day planner service returned ${res.status}`);
          }
          const text = await res.text();
          if (!text.startsWith('{') && !text.startsWith('[')) {
            throw new Error('Day planner service returned an unexpected response');
          }
          return JSON.parse(text);
        } catch (err: any) {
          if (err?.name === 'AbortError') throw new Error('Day planner timed out — try again in a moment');
          throw err;
        }
      })();

      // Server-only generation. There is intentionally no browser-side Gemini
      // fallback — that would require shipping the API key in the bundle.
      const result: DayPlan | null = await proxyPromise;

      if (!result?.stops || result.stops.length === 0) throw new Error('No stops were generated. Please try a different destination or adjust your preferences.');

      // Post-generation: split any conflated venue names ("Venue A & Venue B" → two stops)
      const cleanedStops: DayStop[] = [];
      for (const stop of result.stops) {
        // Detect conflation: "Place A & Place B" or "Place A and Place B"
        const conflationMatch = stop.name.match(/^(.+?)\s+(&|and)\s+(.+)$/i);
        if (conflationMatch && conflationMatch[1].length > 3 && conflationMatch[3].length > 3) {
          // Split into two stops
          cleanedStops.push({ ...stop, name: conflationMatch[1].trim(), description: stop.description.split('.')[0] + '.' });
          cleanedStops.push({ ...stop, name: conflationMatch[3].trim(), time: '', description: stop.description.split('.').slice(1).join('.').trim() || stop.description });
        } else {
          cleanedStops.push(stop);
        }
      }
      result.stops = cleanedStops;

      setPlan(result);
      setStep('result');
      trackDayPlanGenerated(data.destination);
      trackFunnel('generate_success', { source: 'day-planner', destination: data.destination });
    } catch (err: any) {
      console.error('Day planner error:', err);
      trackGenerateFail('day_plan', err?.message || 'unknown', data.destination);
      setError(err.message || 'Something went wrong');
      setStep('error');
    }
  }, []);

  const handleReset = useCallback(() => {
    setStep('form');
    setPlan(null);
    setFormData(null);
    setError(null);
  }, []);

  return (
    <div className="min-h-screen bg-charcoal text-ivory">
      <ScrollToTop />
      <SiteNav />

      {step === 'form' && <DayPlanForm onSubmit={handleSubmit} />}
      {step === 'loading' && formData && <DayPlanLoading formData={formData} />}
      {step === 'result' && plan && formData && <DayPlanResult plan={plan} formData={formData} onReset={handleReset} savedPlanId={savedPlanId} onSave={handleSave} isSaving={isSaving} />}
      {step === 'error' && (
        <div className="min-h-[60vh] flex flex-col items-center justify-center px-8 text-center gap-6">
          <div className="w-12 h-px bg-gold mx-auto" />
          <h2 className="font-display text-2xl text-ivory" style={{ fontWeight: 300 }}>This is taking longer than expected</h2>
          <p className="font-sans text-sm text-ivory/50 max-w-md">{error || 'We couldn\'t finish your day plan in the browser.'} Leave your email and we'll generate it on our end and send it over.</p>
          <RecoveryCapture formData={formData} onReset={handleReset} />
        </div>
      )}

      <SiteFooter />
    </div>
  );
};

export default DayPlannerPage;
